import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {toast} from 'react-toastify';
import {IValidationErrorObject} from '../../../incase-domain/common/validationErrorResponse';
import {IElementStaticList} from '../../../incase-domain/elements/staticList';
import {isDeleteButtonDisabled, randomString, removeArrayElement, swapArrayElement, swapArrayIndices} from '../../../utils/utils';
import TooltipWrapper from '../../../ui/components/common/TooltipWrapper';
import {MAX_NUMBER_OF_LIST_ITEMS, TOOLTIP_DELAY} from '../../../constants';
import {generateErrorMessages, getErrorClasses} from '../../../utils/validation';
import ElementShadow from './common/elementShadow';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from './common/fontAndBackgroundColorPicker';
import FontSelect from './common/fontSelect';
import BorderRadiusChange from './common/borderRadiusChange';
import {TABS} from '../form/part/tabs';
import AnimationForm from './common/animationForm';
import ChecklistTile from '../../../incase-domain/components/checkbox/ChecklistTile';
import SectionTitle from '../../elements/common/SectionTitle';
import DebouncedNumberInput from './common/debounced/DebounceNumberInput';
import DebouncedTextInput from './common/debounced/DebouncedTextInput';
import {LIST_ITEM_MAX} from '../../../incase-domain/validators/elements/constant';
import {useHighlightedElement, useSetHighlightedElement} from '../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedStaticListElementComponentProps {
    errors: IValidationErrorObject | null;
    overrideHighlightedElement?: IElementStaticList;
    overrideHighlightedElementChangeHandler?: {single: (field: keyof IElementStaticList | string, value: unknown) => void};
    activeTab: string;
}
function HighlightedStaticListElementComponent(props: HighlightedStaticListElementComponentProps): ReactElement {
    const {errors, overrideHighlightedElement, overrideHighlightedElementChangeHandler, activeTab} = props;
    const highlightedElement = useHighlightedElement() as IElementStaticList;
    const element = overrideHighlightedElement || highlightedElement;
    const {font} = element;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();
    const changeHandler = overrideHighlightedElementChangeHandler || updateHighlightedElement;

    function swapItemOrderHandler(newIndex: number, oldIndex: number): void {
        const newList = swapArrayIndices(element.items, newIndex, oldIndex);
        changeHandler.single('items', newList);
    }

    function itemChangeHandler(index: number, value: unknown): void {
        const newItems = swapArrayElement(element.items, index, value);
        changeHandler.single('items', newItems);
    }

    function addItem(): void {
        const newItems = [...element.items, ''];
        changeHandler.single('items', newItems);
    }

    function removeItem(index: number): void {
        const newItems = removeArrayElement(element.items, index);
        changeHandler.single('items', newItems);
    }

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <div className="l-group--form">
                    <div className="l-group__item l-grid l-grid--noGaps">
                        <div className="l-col:6 u-flex u-v-center">{t('designSidepanel.fontSize')}</div>
                        <div className="l-col:6">
                            <DebouncedNumberInput
                                value={element.font.size || 1}
                                onChange={(newValue: number) => changeHandler.single('font', {...font, size: newValue})}
                                step={0.1}
                                min={0.1}
                            />
                        </div>
                    </div>
                    <FontSelect highlightedElement={element} highlightedElementChangeHandler={changeHandler} />
                </div>

                <div className="l-group--form">
                    <FontAndBackgroundColorPicker
                        highlightedElement={element}
                        highlightedElementChangeHandler={changeHandler}
                        colorFields={[COLOR_FIELDS.BACKGROUND_COLOR, COLOR_FIELDS.FONT_COLOR]}
                    />
                    <BorderRadiusChange
                        borderRadius={element.borderRadius}
                        highlightedElementChangeHandler={(value) => changeHandler.single('borderRadius', value)}
                    />
                </div>

                <ElementShadow highlightedElement={element} highlightedElementChangeHandler={changeHandler} />

                <AnimationForm
                    appearAfter={element.appearAfter || 0}
                    elementAnimations={element.elementAnimations}
                    highlightedElementChangeHandler={changeHandler}
                />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return <></>;
    }

    return (
        <>
            <div className="l-group--form">
                <div className={`l-group__item ${getErrorClasses(errors, 'items')}`}>
                    <div className="u-flex u-h-between u-v-center">
                        <SectionTitle label={t('designSidepanel.items')} />
                        <button
                            type="button"
                            className="btn-swapOrder"
                            onClick={() => {
                                if (element.items.length < MAX_NUMBER_OF_LIST_ITEMS) addItem();
                                else toast.error(t('designSidepanel.tooManyItems'));
                            }}
                        >
                            {t('designSidepanel.addItem')} +
                        </button>
                    </div>

                    <DragDropContext
                        onDragEnd={(e) => {
                            if (typeof e.destination?.index !== 'undefined') swapItemOrderHandler(e.destination.index, e.source.index);
                        }}
                    >
                        <Droppable droppableId={element.id || randomString(10)}>
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {element.items.map((item, index) => {
                                        return (
                                            <Draggable
                                                key={`${element.id}-static-list-draggable-${index}`}
                                                draggableId={String(index)}
                                                index={index}
                                            >
                                                {(provided) => {
                                                    return (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className="u-mt-2"
                                                            key={`highlighted-element-answer-${index}`}
                                                        >
                                                            <div className=" l-grid l-grid--noGaps">
                                                                <div className="l-col:10">
                                                                    <DebouncedTextInput
                                                                        className="e-input is-sm"
                                                                        value={item}
                                                                        onChange={(value) => itemChangeHandler(index, value)}
                                                                        placeholder={t('designSidepanel.enterLabel')}
                                                                        maxLength={LIST_ITEM_MAX}
                                                                    />
                                                                </div>
                                                                <div className="l-col:2 u-flex u-h-right u-v-center">
                                                                    <TooltipWrapper
                                                                        id="staticList-removeItem"
                                                                        content={t('designSidepanel.removeItem')}
                                                                        delayShow={TOOLTIP_DELAY}
                                                                    >
                                                                        <button
                                                                            aria-label="Remove list item"
                                                                            disabled={isDeleteButtonDisabled(element.items.length, 2)}
                                                                            type="button"
                                                                            onClick={() => removeItem(index)}
                                                                            className="p-presEdit__staticList_removeItem"
                                                                        />
                                                                    </TooltipWrapper>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            </Draggable>
                                        );
                                    })}

                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>

                    {generateErrorMessages(errors, 'items')}
                </div>
            </div>

            <div className="l-group--form">
                <div className="l-group__item">
                    <ChecklistTile
                        value={element.ordered}
                        label={t('designSidepanel.ordered')}
                        onChange={(value) => changeHandler.single('ordered', value)}
                    />
                </div>
            </div>
        </>
    );
}

export default HighlightedStaticListElementComponent;
